import React, { useState, useEffect, startTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Box,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { ContentType, HttpResponse, HTTPValidationError, Project, ProjectCreate } from 'src/api/Api';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { RegistryList, ProjectTypeList } from 'src/dropdown-config';
// import { calculateCreditingPeriod } from 'src/util/util';
import { useAppState } from 'src/state-management/state-provider';
import { countryList } from 'src/constants/countryList';

// Import SDG images
import SDG1 from 'src/resources/images/SDG1.png';
import SDG2 from 'src/resources/images/SDG2.png';
import SDG3 from 'src/resources/images/SDG5.png'; // Corresponds to the 3rd SDG image slot in ProjectDetailComponent
import SDG4 from 'src/resources/images/SDG8.png'; // Corresponds to the 4th SDG image slot
import SDG5 from 'src/resources/images/SDG13.png';
import SDG6 from 'src/resources/images/SDG14.png';
import SDG7 from 'src/resources/images/SDG15.png';

const initalState: ProjectCreate = {
  project_name: '',
  project_type: '',
  has_carbon_module: false,
  has_biodiversity_module: false,
  has_environmental_module: false,
  project_developer: '',
  sdg: [], // Initialize sdg as an empty array
  org_id: '',
};

// Define an array for SDG options
const sdgOptionsList = [
  { id: 'SDG1', src: SDG1, alt: 'SDG 1: No Poverty' },
  { id: 'SDG2', src: SDG2, alt: 'SDG 2: Zero Hunger' },
  { id: 'SDG5', src: SDG3, alt: 'SDG 5: Good Health and Well-being' },
  { id: 'SDG8', src: SDG4, alt: 'SDG 8: Quality Education' },
  { id: 'SDG13', src: SDG5, alt: 'SDG 13: Gender Equality' },
  { id: 'SDG14', src: SDG6, alt: 'SDG 14: Clean Water and Sanitation' },
  { id: 'SDG15', src: SDG7, alt: 'SDG 15: Affordable and Clean Energy' },
];

type ProjectDialogProps = {
  open: boolean;
  onClose: () => void;
  project?: Project;
};

interface ProjectSessionResponse {
  session_id: string;
  project_id: string;
  session_name: string;
  session_start_date: string;
  session_end_date?: string | null;
  status: 'upcoming' | 'active' | 'completed';
}

const ProjectDialog = ({ open, onClose, project }: ProjectDialogProps) => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();

  const [formData, setFormData] = useState<ProjectCreate | Partial<Project>>(initalState);

  useEffect(() => {
    if (project) {
      setFormData({ ...project, sdg: project.sdg || [] }); // Initialize sdg from project or empty array
    } else {
      // When creating a new project, or when project is undefined, reset to initial state
      setFormData({ ...initalState });
    }
  }, [project, open]); // Ensure dialog reset when 'open' changes

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSdgSelection = (sdgId: string) => {
    setFormData((prev) => {
      const currentSdgs = Array.isArray(prev.sdg) ? prev.sdg : [];
      const newSdgs = currentSdgs.includes(sdgId)
        ? currentSdgs.filter((s) => s !== sdgId)
        : [...currentSdgs, sdgId];
      return { ...prev, sdg: newSdgs };
    });
  };

  const validateProjectDates = (): boolean => {
    if (!formData.project_end_date) {
      showSnackbar({
        message: 'Please select a project end date.',
        type: snackbarType.ERROR,
      });
      return false;
    }

    if (!formData.project_start_date) {
      return true;
    }

    const startDate = new Date(formData.project_start_date as string);
    const endDate = new Date(formData.project_end_date as string);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      showSnackbar({
        message: 'Invalid format for project start or end date.',
        type: snackbarType.ERROR,
      });
      return false;
    }

    if (endDate < startDate) {
      showSnackbar({
        message: 'Project end date must be equal to or later than the start date.',
        type: snackbarType.ERROR,
      });
      return false;
    }

    return true;
  };

  const duplicateProjectName = (name: string) => {
    return state.projects.some((p) => p.project_name.toLowerCase() === name.toLowerCase());
  };

  const mapProjectTypeToBackend = (type?: string): 'Agroforestry' | 'Conservation' => {
    if (!type || !type.trim()) return 'Conservation';
    if (type === 'Agroforestry' || type === 'Conservation') return type;

    const lower = type.toLowerCase();
    // Keywords representing tree planting, agroforestry, reforestation, or agriculture
    if (
      lower.includes('afforest') ||
      lower.includes('reforest') ||
      lower.includes('reveget') ||
      lower.includes('agri') ||
      lower.includes('farm') ||
      lower.includes('arr') ||
      lower.includes('alm') ||
      lower.includes('tree')
    ) {
      return 'Agroforestry';
    }

    // Default to 'Conservation' for all other restoration, management, protection, or newly added types
    return 'Conservation';
  };

  const createInitialProjectSession = async (createdProject: Project): Promise<ProjectSessionResponse | null> => {
    const startDate = formData.project_start_date as string | undefined;
    if (!startDate) return null;

    const response = await clientService.request<ProjectSessionResponse, HTTPValidationError>({
      path: `/api/v1/projects/${createdProject.project_id}/sessions`,
      method: 'POST',
      body: {
        session_name: 'Initial Session',
        session_start_date: startDate,
        session_end_date: (formData.project_end_date as string | undefined) || null,
        status: 'upcoming',
      },
      type: ContentType.Json,
      format: 'json',
      secure: true,
    });
    return response.data;
  };

  const handleSubmit = () => {
    const anyAnalysisType =
      formData.has_carbon_module ||
      formData.has_biodiversity_module ||
      formData.has_environmental_module;
    if (!formData.project_name || !formData.project_developer || !anyAnalysisType) {
      showSnackbar({
        message: 'Please fill in all mandatory fields.',
        type: snackbarType.ERROR,
      });
      return;
    }

    if (!project && !formData.project_start_date) {
      showSnackbar({
        message: 'Please fill in the project start date to create the first session.',
        type: snackbarType.ERROR,
      });
      return;
    }

    if (!project && duplicateProjectName(formData.project_name)) {
      showSnackbar({
        message: 'Project name already exists. Please choose a different name.',
        type: snackbarType.ERROR,
      });
      return;
    }

    // Validate project dates if both are provided
    if (!validateProjectDates()) {
      return;
    }
    // Map analysis_type back to boolean fields if your API expects them

    if (project) {
      // Update existing project

      // Validate credit period dates if both are provided
      // if (formData.project_start_date && formData.project_end_date) {
      //   const startDate = new Date(formData.project_start_date as string);
      //   const endDate = new Date(formData.project_end_date as string);

      //   if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      //     showSnackbar({
      //       message: 'Invalid format for credit period start or end date.',
      //       type: snackbarType.ERROR,
      //     });
      //     return;
      //   }

      //   if (endDate <= startDate) {
      //     showSnackbar({
      //       message: 'Credit period end date must be after the start date.',
      //       type: snackbarType.ERROR,
      //     });
      //     return;
      //   }
      // }

      const updatePayload = {
        ...formData,
        project_type: mapProjectTypeToBackend(formData.project_type as string),
        project_start_date: (formData.project_start_date as string) || undefined,
        project_end_date: (formData.project_end_date as string) || undefined,
      } as ProjectCreate;

      // if (formData.project_start_date && formData.project_end_date) {
      //   // At this point, dates are valid and endDate > startDate
      //   const creditingPeriod = calculateCreditingPeriod(formData.project_start_date as string, formData.project_end_date as string);
      //   if (creditingPeriod) { // creditingPeriod should be a string here
      //     updatePayload = { ...updatePayload, crediting_period: creditingPeriod };
      //   }
      // }

      clientService.api
        .updateProjectApiV1ProjectsProjectIdPut(project.project_id, updatePayload as ProjectCreate)
        .then((updatedProject: HttpResponse<Project, HTTPValidationError>) => {
          dispatch({ type: 'SET_PROJECT_DETAILS', payload: updatedProject.data });
          showSnackbar({ message: 'Project updated successfully!' });
          setFormData(updatedProject.data);
        })
        .catch((error) => {
          console.error('Error updating project:', error);
          showSnackbar({
            message:
              'Error updating project:' +
              (error.error?.detail || error.message || 'Unknown error.'),
            type: snackbarType.ERROR,
          });
        });
    } else {
      // Create new project
      const createPayload = {
        ...formData,
        project_type: mapProjectTypeToBackend(formData.project_type as string),
        project_start_date: (formData.project_start_date as string) || undefined,
        project_end_date: (formData.project_end_date as string) || undefined,
      } as ProjectCreate;
      clientService.api
        .createProjectApiV1ProjectsPost(createPayload as ProjectCreate)
        .then(async (newProject: HttpResponse<Project, HTTPValidationError>) => {
          startTransition(() => {
            dispatch({ type: 'SET_PROJECT_LIST', payload: [newProject.data, ...state.projects] });
          });
          try {
            const initialSession = await createInitialProjectSession(newProject.data);
            if (initialSession) {
              localStorage.setItem(
                'activeProjectSessionSelection',
                JSON.stringify({
                  projectId: newProject.data.project_id,
                  session: initialSession,
                })
              );
            }
            showSnackbar({ message: 'Project and initial session added successfully!' });
          } catch (sessionError) {
            console.error('Error creating initial project session:', sessionError);
            showSnackbar({
              message: 'Project added, but initial session could not be created.',
              type: snackbarType.ERROR,
            });
          }
          setFormData(initalState);
        })
        .catch((error) => {
          console.error('Error adding project:', error);
          const errorDetail = error.error?.detail;
          const errorMsg = typeof errorDetail === 'string' ? errorDetail :
            (Array.isArray(errorDetail) ? JSON.stringify(errorDetail) : (error.message || 'Unknown error.'));
          showSnackbar({
            message: 'Error adding project: ' + errorMsg,
            type: snackbarType.ERROR,
          });
        });
    }
    // onSubmit(formData);
    onClose();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#0A0A0A',
          backgroundImage: 'none',
          color: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #222',
          overflow: 'hidden', // Prevent paper scroll
        }
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 1.5, fontSize: '1.5rem', fontWeight: 700, color: '#FFFFFF', position: 'relative' }}>
        {project ? 'Update Project Details' : 'Create New Project'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: '#666', '&:hover': { color: '#FFF' }, zIndex: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 3,
          pt: 0,
          pb: 1,
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {project && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 2, fontWeight: 600, color: '#EAB308' }}>
              Sustainable Development Goals (SDGs)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                mb: 4,
                justifyContent: 'flex-start',
              }}
            >
              {sdgOptionsList.map((sdg) => (
                <Box
                  key={sdg.id}
                  component="img"
                  src={sdg.src}
                  alt={sdg.alt}
                  title={sdg.alt}
                  onClick={() => handleSdgSelection(sdg.id)}
                  sx={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: formData.sdg?.includes(sdg.id)
                      ? '2px solid #10B981'
                      : '2px solid #222',
                    padding: '4px',
                    backgroundColor: formData.sdg?.includes(sdg.id) ? 'rgba(16, 185, 129, 0.1)' : '#111',
                    filter: formData.sdg?.includes(sdg.id) ? 'none' : 'grayscale(1)',
                    '&:hover': {
                      borderColor: '#10B981',
                      transform: 'scale(1.05)',
                      filter: 'none',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
              ))}
            </Box>
          </>
        )}

        <TextField
          label="Project Name"
          value={formData.project_name || ''}
          onChange={(e) => handleChange('project_name', e.target.value)}
          fullWidth
          margin="normal"
          required
          disabled={!!project}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#FFF',
              borderRadius: '8px',
              '& fieldset': { borderColor: '#333' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
            '& .MuiInputLabel-root': { color: '#666' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
          }}
        />
        <TextField
          label="Project Developer"
          value={formData.project_developer || ''}
          onChange={(e) => handleChange('project_developer', e.target.value)}
          fullWidth
          margin="normal"
          required
          disabled={!!project}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#FFF',
              borderRadius: '8px',
              '& fieldset': { borderColor: '#333' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
            '& .MuiInputLabel-root': { color: '#666' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
          }}
        />
        <TextField
          label="Project Start Date"
          type="date"
          value={formData.project_start_date || ''}
          onChange={(e) => handleChange('project_start_date', e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#FFF',
              borderRadius: '8px',
              '& fieldset': { borderColor: '#333' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
              '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
            },
            '& .MuiInputLabel-root': { color: '#666' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
          }}
        />

        <TextField
          label="Project End Date"
          type="date"
          value={formData.project_end_date || ''}
          onChange={(e) => handleChange('project_end_date', e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{
            inputLabel: { shrink: true },
          }}
          inputProps={{
            min: formData.project_start_date
              ? (formData.project_start_date as string).split('T')[0]
              : undefined,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#FFF',
              borderRadius: '8px',
              '& fieldset': { borderColor: '#333' },
              '&:hover fieldset': { borderColor: '#444' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
              '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
            },
            '& .MuiInputLabel-root': { color: '#666' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
          }}
        />

        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_carbon_module}
                onChange={handleCheckboxChange}
                name="has_carbon_module"
                sx={{ color: '#333', '&.Mui-checked': { color: '#10B981' } }}
              />
            }
            label={<Typography variant="body2" sx={{ color: '#999' }}>Carbon Analysis</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_biodiversity_module}
                onChange={handleCheckboxChange}
                name="has_biodiversity_module"
                sx={{ color: '#333', '&.Mui-checked': { color: '#10B981' } }}
              />
            }
            label={<Typography variant="body2" sx={{ color: '#999' }}>Biodiversity Analysis</Typography>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.has_environmental_module}
                onChange={handleCheckboxChange}
                name="has_environmental_module"
                sx={{ color: '#333', '&.Mui-checked': { color: '#10B981' } }}
              />
            }
            label={<Typography variant="body2" sx={{ color: '#999' }}>Climate Analysis</Typography>}
          />
        </Box>
        {formData.has_carbon_module && (
          <TextField
            label="Carbon Registry"
            value={formData.registry || ''}
            onChange={(e) => handleChange('registry', e.target.value)}
            select
            fullWidth
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#151515',
                color: '#FFF',
                borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#666' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
              '& .MuiSvgIcon-root': { color: '#666' },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' }
                }
              }
            }}
          >
            {RegistryList.map((item) => (
              <MenuItem key={item.value} value={item.value} sx={{ '&:hover': { backgroundColor: '#222' } }}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {formData.has_carbon_module && (
          <TextField
            label="Project Type"
            value={formData.project_type || ''}
            onChange={(e) => handleChange('project_type', e.target.value)}
            select
            fullWidth
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#151515',
                color: '#FFF',
                borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#666' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
              '& .MuiSvgIcon-root': { color: '#666' },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' }
                }
              }
            }}
          >
            {formData.registry &&
              ProjectTypeList[formData.registry]?.map((child) => (
                <MenuItem key={child.name} value={child.name} sx={{ '&:hover': { backgroundColor: '#222' } }}>
                  {child.name}
                </MenuItem>
              ))}
            {formData.project_type &&
              !ProjectTypeList[formData.registry || '']?.some((c) => c.name === formData.project_type) && (
                <MenuItem key={formData.project_type} value={formData.project_type} sx={{ '&:hover': { backgroundColor: '#222' } }}>
                  {formData.project_type}
                </MenuItem>
              )}
          </TextField>
        )}
        {project && (
          <>
            <Autocomplete
              id="country-select-demo"
              options={countryList}
              autoHighlight
              getOptionLabel={(option) => option.label}
              value={countryList.find((option) => option.countryCode2 === formData.country)}
              onChange={(event, newValue) => {
                handleChange('country', newValue?.countryCode2);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    sx={{ backgroundColor: '#151515', color: '#FFF', '&:hover': { backgroundColor: '#222 !important' }, '& > img': { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    <img
                      loading="lazy"
                      width="20"
                      srcSet={`https://flagcdn.com/w40/${option.countryCode2.toLowerCase()}.png 2x`}
                      src={`https://flagcdn.com/w20/${option.countryCode2.toLowerCase()}.png`}
                      alt=""
                    />
                    {option.label} ({option.countryCode2})
                  </Box>
                );
              }}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#151515',
                  color: '#FFF',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#333' },
                  '&:hover fieldset': { borderColor: '#444' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#666' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
                '& .MuiSvgIcon-root': { color: '#666' },
              }}
              slotProps={{
                paper: { sx: { backgroundColor: '#151515', color: '#FFF', border: '1px solid #333' } }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose a country"
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      autoComplete: 'new-password',
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Project Description"
              value={formData.project_description || ''}
              onChange={(e) => handleChange('project_description', e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={7}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#151515',
                  color: '#FFF',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#333' },
                  '&:hover fieldset': { borderColor: '#444' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#666' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1.5, gap: 2, borderTop: '1px solid #222' }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#999',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#FFF', backgroundColor: 'transparent' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#10B981',
            color: '#FFF',
            textTransform: 'none',
            fontWeight: 700,
            px: 4,
            borderRadius: '8px',
            '&:hover': { backgroundColor: '#059669' }
          }}
        >
          {project ? 'Update' : 'Confirm & Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDialog;
